import React from 'react';
import { Handshake, Truck, WarningCircle } from '@phosphor-icons/react';

import {
  Checkbox,
  Options,
  OptionRow,
  Pill,
  SectionTitle,
} from '../../design-system/components/desktop';
import { Icon } from '../../design-system/components/Icon';

/**
 * The fulfilment picker.
 *
 * LOCKED (`canSelectShipping=false`, the prop default; `Checkout` no longer
 * uses it now that shipping is open to every buyer): "Buy in person" is the only usable method and "Ship to me" is an inert row
 * carrying a "Coming soon" pill. Neither row is a control and neither claims
 * `aria-pressed`, because a toggle that cannot be toggled is a lie told to a
 * screen reader. `queryByRole('button')` therefore finds nothing in this
 * branch, and the only widget in the section is the acknowledgement checkbox.
 *
 * OPEN (`canSelectShipping=true`, what `Checkout` passes): both rows become real
 * controls and drive the parent's `excludeDelivery` flag through `onSelect`.
 * Only then do they take `role="button"`, a tab stop and `aria-pressed`, so the
 * toggle is claimed exactly when it exists. `role="button"` is not a `<button>`,
 * so Enter and Space have to be wired by hand; that is all `handleTileKeyDown`
 * is for.
 *
 * Selection state lives in `Checkout` (the existing `excludeDelivery` flag), so
 * this component owns only the acknowledgement wiring; `selected` is
 * 'in_person' | 'ship'. `highlight` is set by `Checkout` when the buyer presses
 * Place Order without ticking the confirmation: it draws attention to the
 * checkbox instead of disabling the button.
 *
 * THE WARNING IS THE POINT OF THIS COMPONENT. It is the one piece of copy on
 * the page that protects real money, so it keeps a warning-tinted surface, an
 * icon AND wording that carries the meaning on its own. Colour is never the
 * message. It appears, with the required acknowledgement, whenever in-person is
 * the ACTIVE choice. With shipping locked there is no other choice to make, so
 * in-person counts as active whatever `selected` happens to say.
 *
 * A selectable row needs no new CSS. `.dsk-option-row` is already a pointer
 * target, `.is-selected` is the library's accent tint for a row that carries no
 * input, and `:focus-within` matches the focused row itself, so the keyboard
 * ring lands at row scale.
 */
const FulfillmentSelector = ({
  acknowledged,
  onAcknowledgeChange,
  highlight = false,
  canSelectShipping = false,
  selected = 'in_person',
  onSelect = () => {},
}) => {
  const inPersonActive = !canSelectShipping || selected === 'in_person';
  const shipActive = selected === 'ship';

  const handleTileKeyDown = (method) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(method);
    }
  };

  // A row becomes a control, a tab stop and a pressed state ONLY when there is
  // something to pick. Otherwise it stays the inert row it has always been.
  const tileProps = (method, active) =>
    canSelectShipping
      ? {
          role: 'button',
          tabIndex: 0,
          'aria-pressed': active,
          onClick: () => onSelect(method),
          onKeyDown: handleTileKeyDown(method),
        }
      : {};

  return (
    <section className="dsa-checkout__section">
      <SectionTitle>How do you want it?</SectionTitle>

      <Options>
        <OptionRow selected={inPersonActive} {...tileProps('in_person', inPersonActive)}>
          <span className="dsk-option-row__body">
            <span className="dsk-option-row__title">Buy in person</span>
            <span className="dsk-option-row__sub">
              Collect the cards and pay the seller face to face.
            </span>
          </span>
          <Icon as={Handshake} size="lg" />
        </OptionRow>

        {canSelectShipping ? (
          <OptionRow selected={shipActive} {...tileProps('ship', shipActive)}>
            <span className="dsk-option-row__body">
              <span className="dsk-option-row__title">Ship to me</span>
              <span className="dsk-option-row__sub">
                Delivered nationwide across South Africa.
              </span>
            </span>
            <Icon as={Truck} size="lg" />
          </OptionRow>
        ) : (
          /* `aria-disabled` is deliberately NOT here. `OptionRow` renders a
             <label>, and in this branch it wraps no form control — a
             control-less label has no mapped ARIA role, so `aria-disabled` on
             it is inert markup that reads as a guarantee in the source and
             delivers nothing. The visible "Coming soon" pill IS the mechanism,
             and it is real text a screen reader reaches in browse mode. */
          <OptionRow className="is-disabled dsa-checkout__fulfil--soon">
            <span className="dsk-option-row__body">
              <span className="dsk-option-row__title">Ship to me</span>
              <span className="dsk-option-row__sub">Delivered nationwide across South Africa.</span>
            </span>
            <Pill>Coming soon</Pill>
            <Icon as={Truck} size="lg" />
          </OptionRow>
        )}
      </Options>

      {inPersonActive && (
        <>
          {/* THE WARNING COPY, AS DICTATED VS AS WRITTEN.
           *
           * Junaid's words, voice-to-text and lightly garbled:
           *
           *   "This only uses sellers' writing. The seller must have the card in
           *    hand and have confirmed they will hand it over. Check the card's
           *    condition against your order before and change his hands. Payment
           *    is released immediately. There is no shipping, no protection if
           *    you're not face-to-face."
           *
           * Two transcription fixes, ruled on before writing: "sellers' writing"
           * is sellers' RATINGS — the buyer's only assurance is the seller's
           * reputation — and "before and change his hands" is "before it changes
           * hands". Everything else is his, in his order.
           *
           * The ratings sentence is the one FACT the previous copy was missing,
           * and it is the load-bearing one: it names what stands in for the
           * buyer protection the next sentence takes away. The old copy said
           * "no protection" and then stopped, which tells a buyer they have
           * nothing when in fact they have one thing, and it is checkable.
           *
           * "the set number" is dropped from the condition check. He asked for
           * condition against the order; the order line already names the card. */}
          <div className="dsa-checkout__warn" role="note">
            <Icon as={WarningCircle} size="lg" />
            <div>
              <p className="dsa-checkout__warn-title">
                Only use this if the seller is right in front of you
              </p>
              <p>
                The seller must have the card in hand and have confirmed they will hand it over
                now. Check the card's condition against your order before it changes hands.
              </p>
              <p>
                Payment is released immediately. There is no shipping and no buyer protection if
                you are not face to face — the seller's rating is the only assurance you have.
              </p>
            </div>
          </div>

          <Options className="dsa-checkout__ack">
            <OptionRow
              className={highlight ? 'dsa-checkout__ack--attention' : undefined}
              control={
                <Checkbox
                  checked={acknowledged}
                  aria-invalid={highlight}
                  onChange={(e) => onAcknowledgeChange(e.target.checked)}
                />
              }
              title="The seller is with me now and is handing the card over in person."
            />
          </Options>

          {highlight && (
            <p className="dsk-error dsa-checkout__ack-hint" role="alert">
              Please confirm this before placing your order.
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default FulfillmentSelector;
